/**
 * Dirty rows manager for the Fluxo Gerencial batch-edit mode.
 *
 * Holds in-memory edits AND pending deletes keyed by row Id.
 * Decisions:
 *   - In-memory only (no localStorage). beforeunload warning when dirty.
 *   - Per-cell undo stack (cap 50). Ctrl+Z pops one cell change.
 *   - Validation errors live alongside the dirty map so the toolbar can
 *     show "X linhas alteradas · ⚠️ Y erros" and disable Save when errors > 0.
 *   - A row marked for delete is NOT editable — its dirty cell entries are
 *     ignored on save (deletes win over conflicting updates).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type EditableField =
  | 'Data'
  | 'Valor'
  | 'CentroResultado'
  | 'NumeroNota'
  | 'Natureza'
  | 'CentroCusto'
  | 'Empresa'
  | 'Descricao'

export type CellValue = string | number | Date | null

export interface CellChange {
  rowId: number
  field: EditableField
  prevValue: CellValue
  nextValue: CellValue
}

export type DirtyRowMap = Map<number, Partial<Record<EditableField, CellValue>>>

export type ValidationErrors = Map<number, Partial<Record<EditableField, string>>>

const UNDO_CAP = 50

function cellValueEquals(a: CellValue, b: CellValue): boolean {
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()
  return Object.is(a, b)
}

export interface BatchPayload {
  updates: Array<{ id: number; fields: Partial<Record<EditableField, CellValue>> }>
  deletes: number[]
}

export interface UseDirtyRowsResult {
  dirty: DirtyRowMap
  errors: ValidationErrors
  pendingDeletes: Set<number>

  updateCount: number
  deleteCount: number
  errorCount: number
  /** Total of updates + deletes — used by toolbar counters and `canSave`. */
  changeCount: number
  hasDirty: boolean
  canSave: boolean

  /** Set a cell value. Records prev→next on the undo stack. No-op if row is marked for delete. */
  setCell: (rowId: number, field: EditableField, prevValue: CellValue, nextValue: CellValue) => void
  /** Set/clear a validation error for a cell. */
  setError: (rowId: number, field: EditableField, message: string | null) => void
  /** Mark/unmark a row for deletion. Toggling off restores any prior cell edits. */
  toggleDelete: (rowId: number) => void
  /** Bulk-mark a set of rows for deletion (idempotent — already-marked rows stay marked). */
  markForDelete: (rowIds: Iterable<number>) => void
  /** Pop the last change from the undo stack and revert it. */
  undo: () => CellChange | null
  /** Drop everything — used by Cancelar edição and after successful Save. */
  reset: () => void
  /** Build the payload for the batch-update mutation. */
  buildPayload: () => BatchPayload
  /** Read the current edited value for a cell, or undefined if untouched. */
  getCell: (rowId: number, field: EditableField) => CellValue | undefined
  /** Read the validation error message for a cell, or undefined. */
  getError: (rowId: number, field: EditableField) => string | undefined
  /** Whether a row is currently marked for delete. */
  isMarkedForDelete: (rowId: number) => boolean
}

export function useDirtyRows(): UseDirtyRowsResult {
  const [dirty, setDirty] = useState<DirtyRowMap>(() => new Map())
  const [errors, setErrors] = useState<ValidationErrors>(() => new Map())
  const [pendingDeletes, setPendingDeletes] = useState<Set<number>>(() => new Set())
  const undoStackRef = useRef<CellChange[]>([])

  const updateCount = dirty.size
  const deleteCount = pendingDeletes.size
  const errorCount = useMemo(() => {
    let total = 0
    errors.forEach(rowErrors => { total += Object.keys(rowErrors).length })
    return total
  }, [errors])
  const changeCount = updateCount + deleteCount
  const hasDirty = changeCount > 0
  const canSave = hasDirty && errorCount === 0

  const setCell = useCallback((rowId: number, field: EditableField, prevValue: CellValue, nextValue: CellValue) => {
    // No edits on rows scheduled for delete — UI should already disable inputs,
    // but this is defensive against keystrokes that slip through.
    if (pendingDeletes.has(rowId)) return
    if (cellValueEquals(prevValue, nextValue)) return
    setDirty(prev => {
      const next = new Map(prev)
      const row = { ...(next.get(rowId) ?? {}) }
      row[field] = nextValue
      next.set(rowId, row)
      return next
    })
    const stack = undoStackRef.current
    stack.push({ rowId, field, prevValue, nextValue })
    if (stack.length > UNDO_CAP) stack.shift()
  }, [pendingDeletes])

  const setError = useCallback((rowId: number, field: EditableField, message: string | null) => {
    setErrors(prev => {
      const next = new Map(prev)
      const row = { ...(next.get(rowId) ?? {}) }
      if (message) {
        row[field] = message
      } else {
        delete row[field]
      }
      if (Object.keys(row).length === 0) next.delete(rowId)
      else next.set(rowId, row)
      return next
    })
  }, [])

  const toggleDelete = useCallback((rowId: number) => {
    setPendingDeletes(prev => {
      const next = new Set(prev)
      if (next.has(rowId)) next.delete(rowId)
      else {
        next.add(rowId)
        // Clear any validation errors for this row — deleting moots them.
        setErrors(prevErr => {
          if (!prevErr.has(rowId)) return prevErr
          const ne = new Map(prevErr)
          ne.delete(rowId)
          return ne
        })
      }
      return next
    })
  }, [])

  const markForDelete = useCallback((rowIds: Iterable<number>) => {
    const ids = Array.from(rowIds)
    if (ids.length === 0) return
    setPendingDeletes(prev => {
      const next = new Set(prev)
      for (const id of ids) next.add(id)
      return next
    })
    // Clear validation errors for the bulk-deleted rows — same rationale as toggleDelete.
    setErrors(prev => {
      let changed = false
      const next = new Map(prev)
      for (const id of ids) {
        if (next.delete(id)) changed = true
      }
      return changed ? next : prev
    })
  }, [])

  const undo = useCallback((): CellChange | null => {
    const stack = undoStackRef.current
    const last = stack.pop()
    if (!last) return null
    setDirty(prev => {
      const next = new Map(prev)
      const row = { ...(next.get(last.rowId) ?? {}) }
      const earlier = stack.slice().reverse().find(c => c.rowId === last.rowId && c.field === last.field)
      if (earlier) {
        row[last.field] = earlier.nextValue
        next.set(last.rowId, row)
      } else {
        delete row[last.field]
        if (Object.keys(row).length === 0) next.delete(last.rowId)
        else next.set(last.rowId, row)
      }
      return next
    })
    return last
  }, [])

  const reset = useCallback(() => {
    setDirty(new Map())
    setErrors(new Map())
    setPendingDeletes(new Set())
    undoStackRef.current = []
  }, [])

  const buildPayload = useCallback((): BatchPayload => {
    const updates: BatchPayload['updates'] = []
    // Skip rows marked for delete — they go into `deletes`, not `updates`.
    dirty.forEach((fields, id) => {
      if (!pendingDeletes.has(id)) updates.push({ id, fields })
    })
    return { updates, deletes: Array.from(pendingDeletes) }
  }, [dirty, pendingDeletes])

  const getCell = useCallback((rowId: number, field: EditableField) => dirty.get(rowId)?.[field], [dirty])
  const getError = useCallback((rowId: number, field: EditableField) => errors.get(rowId)?.[field], [errors])
  const isMarkedForDelete = useCallback((rowId: number) => pendingDeletes.has(rowId), [pendingDeletes])

  // beforeunload guard while there are unsaved edits OR pending deletes.
  useEffect(() => {
    if (!hasDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasDirty])

  return {
    dirty,
    errors,
    pendingDeletes,
    updateCount,
    deleteCount,
    errorCount,
    changeCount,
    hasDirty,
    canSave,
    setCell,
    setError,
    toggleDelete,
    markForDelete,
    undo,
    reset,
    buildPayload,
    getCell,
    getError,
    isMarkedForDelete,
  }
}
